import React, { useEffect, useState } from "react";
import Header from "../../views/layout/Header";
import AdminSideBar from "../../views/layout/AdminSideBar";
import { axiosInstance } from "../../helpers/axios/axios";
import {
  exportPO,
  exportPOInward,
  LogisticsClientsURL,
  LogisticsCompaniesURL,
  LogisticsDownloadMultiplePDFs,
  LogisticsPOInward,
  LogisticsPOPdf,
  LogisticsWarehouse,
} from "../../helpers/endpoints/api_endpoints";
import { Link } from "react-router-dom";
import { Button, Dropdown, Form, Modal, Spinner, Table } from "react-bootstrap";
import { Select } from "antd";
import { toast } from "react-toastify";
import { Download, Loader, Trash2 } from "react-feather";
import AddInwardModal from "./AddInwardModal";
import POExportModal from "./POExportModal";
import { WarningOutlined } from "@ant-design/icons";

// import ExportModal from "./ExportModal";

function ViewDetailsModal({
  show,
  handleClose,
  updateOutwardInList,
  removeOutwardFromList,
}) {
  const [warehouseList, setWarehouseList] = useState([]);
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [productList, setProductList] = useState([]);
  const [clientList, setClientList] = useState([]);

  // Search states for Client and Warehouse
  const [clientQuery, setClientQuery] = useState("");
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [warehouseQuery, setWarehouseQuery] = useState("");
  const [warehouseSuggestions, setWarehouseSuggestions] = useState([]);
  const [stateQuery, setStateQuery] = useState("");
  const [stateSuggestions, setStateSuggestions] = useState([]);

  useEffect(() => {
    if (show) {
      axiosInstance.get(`${LogisticsPOInward}${show}/`).then(
        (response) => {
          console.log(response.data);
          setFormData(response.data);
          setOriginalData(response.data); // Save original data
        },
        (error) => {
          console.log(error);
        }
      );

      // Fetch products data
      axiosInstance.get(LogisticsCompaniesURL).then(
        (response) => {
          setProductList(response.data);
        },
        (error) => {
          console.log(error);
        }
      );

      // Fetch client data
      axiosInstance.get(LogisticsClientsURL).then(
        (response) => {
          setClientList(response.data);
        },
        (error) => {
          console.log(error);
        }
      );
    } else {
      setIsEditing(false);
      setFormData(null);
      setOriginalData(null); // Reset original data
      setClientQuery("");
      setClientSuggestions([]);
      setWarehouseQuery("");
      setWarehouseSuggestions([]);
      setStateQuery("");
      setStateSuggestions([]);
    }
  }, [show]);

  // Fetch client suggestions
  const fetchClientSuggestions = async (query) => {
    try {
      const { data } = await axiosInstance.get(LogisticsClientsURL, {
        params: { name: query },
      });
      setClientSuggestions(data.results || []);
    } catch (err) {
      console.error("Client search error:", err);
      setClientSuggestions([]);
    }
  };

  // Fetch warehouse suggestions
  const fetchWarehouseSuggestions = async (query) => {
    try {
      const { data } = await axiosInstance.get(LogisticsWarehouse, {
        params: { location: query },
      });
      setWarehouseSuggestions(data.results || []);
    } catch (err) {
      console.error("Warehouse search error:", err);
      setWarehouseSuggestions([]);
    }
  };

  // Filter state suggestions based on query
  const filterStateSuggestions = (query) => {
    if (!query) {
      return stateNames;
    }
    return stateNames.filter((state) =>
      state.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Debounce client search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (clientQuery.length) {
        fetchClientSuggestions(clientQuery);
      } else {
        // Show initial client suggestions when no query
        fetchClientSuggestions("");
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [clientQuery]);

  // Debounce warehouse search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (warehouseQuery.length) {
        fetchWarehouseSuggestions(warehouseQuery);
      } else {
        // Show initial warehouse suggestions when no query
        fetchWarehouseSuggestions("");
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [warehouseQuery]);

  // Debounce state search
  useEffect(() => {
    const handler = setTimeout(() => {
      const filteredStates = filterStateSuggestions(stateQuery);
      setStateSuggestions(filteredStates);
    }, 300);
    return () => clearTimeout(handler);
  }, [stateQuery]);

  // Show initial suggestions when input is focused
  const handleClientFocus = () => {
    if (clientSuggestions.length === 0) {
      fetchClientSuggestions("");
    }
  };

  const handleWarehouseFocus = () => {
    if (warehouseSuggestions.length === 0) {
      fetchWarehouseSuggestions("");
    }
  };

  const handleStateFocus = () => {
    if (stateSuggestions.length === 0) {
      setStateSuggestions(stateNames);
    }
  };

  // Clear suggestions when clicking outside
  const handleClickOutside = (e) => {
    if (
      !e.target.closest(".client-search-container") &&
      !e.target.closest(".warehouse-search-container") &&
      !e.target.closest(".state-search-container")
    ) {
      setClientSuggestions([]);
      setWarehouseSuggestions([]);
      setStateSuggestions([]);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSelectChange = (name, value, label) => {
    const selectedValue =
      name === "client"
        ? value
        : label && name === "company"
        ? value
        : label && name === "warehouse"
        ? value
        : label;

    console.log(selectedValue);
    setFormData({ ...formData, [name]: selectedValue });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const changedData = {};
    for (let key in formData) {
      if (formData[key] !== originalData[key]) {
        changedData[key] = formData[key];
      }
    }

    console.log("Changed Data: ", changedData);

    axiosInstance.patch(`${LogisticsPOInward}${show}/`, changedData).then(
      (response) => {
        console.log(response.data);
        updateOutwardInList(response.data);
        setIsEditing(false);
        handleClose();
        toast.success("Saved successfully");
      },
      (error) => {
        console.log(error);
      }
    );
  };

  //   const handleDelete = () => {
  //     axiosInstance.delete(`${inwardFormUrl}${show}/`).then(
  //       () => {
  //         removeOutwardFromList(show);
  //         setShowDeleteConfirmation(false);
  //         handleClose();
  //       },
  //       (error) => {
  //         console.log(error);
  //       }
  //     );
  //   };

  const openDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const ClientList = clientList?.results?.map((company) => ({
    value: company.id,
    label: company?.name,
  }));

  const CompanyList = productList?.results?.map((company) => ({
    value: company.id,
    label: company?.name,
  }));
  const transportType = [
    { value: "1", label: "Air" },
    { value: "2", label: "Shipping" },
    { value: "3", label: "Surface" },
  ];
  const typeDropdownData = [
    { value: "1", label: "ATM Movement" },
    { value: "2", label: "Household" },
    { value: "3", label: "3PL" },
    { value: "4", label: "Other" },
  ];
  const ATMCategory = [
    { value: "1", label: "Primary" },
    { value: "2", label: "Secondary" },
  ];
  const ATMType = [
    { value: "1", label: "Pickup" },
    { value: "2", label: "Buyback" },
    { value: "3", label: "Shifting" },
    { value: "4", label: "Others" },
  ];
  const stateNames = [
    // States
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Arunachal Pradesh", label: "Arunachal Pradesh" },
    { value: "Assam", label: "Assam" },
    { value: "Bihar", label: "Bihar" },
    { value: "Chhattisgarh", label: "Chhattisgarh" },
    { value: "Goa", label: "Goa" },
    { value: "Gujarat", label: "Gujarat" },
    { value: "Haryana", label: "Haryana" },
    { value: "Himachal Pradesh", label: "Himachal Pradesh" },
    { value: "Jharkhand", label: "Jharkhand" },
    { value: "Karnataka", label: "Karnataka" },
    { value: "Kerala", label: "Kerala" },
    { value: "Madhya Pradesh", label: "Madhya Pradesh" },
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Manipur", label: "Manipur" },
    { value: "Meghalaya", label: "Meghalaya" },
    { value: "Mizoram", label: "Mizoram" },
    { value: "Nagaland", label: "Nagaland" },
    { value: "Odisha", label: "Odisha" },
    { value: "Punjab", label: "Punjab" },
    { value: "Rajasthan", label: "Rajasthan" },
    { value: "Sikkim", label: "Sikkim" },
    { value: "Tamil Nadu", label: "Tamil Nadu" },
    { value: "Telangana", label: "Telangana" },
    { value: "Tripura", label: "Tripura" },
    { value: "Uttar Pradesh", label: "Uttar Pradesh" },
    { value: "Uttarakhand", label: "Uttarakhand" },
    { value: "West Bengal", label: "West Bengal" },
    {
      value: "Andaman and Nicobar Islands",
      label: "Andaman and Nicobar Islands",
    },
    { value: "Chandigarh", label: "Chandigarh" },
    { value: "Delhi", label: "Delhi" },
    {
      value: "Dadra and Nagar Haveli and Daman & Diu",
      label: "Dadra and Nagar Haveli and Daman & Diu",
    },
    { value: "Jammu & Kashmir", label: "Jammu & Kashmir" },
    { value: "Ladakh", label: "Ladakh" },
    { value: "Lakshadweep", label: "Lakshadweep" },
    { value: "Puducherry", label: "Puducherry" },
  ];
  // fetch warehouse data
  useEffect(() => {
    axiosInstance.get(`${LogisticsWarehouse}`).then(
      (response) => {
        console.log(response.data);
        setWarehouseList(response.data);
      },
      (error) => {
        console.log(error);
      }
    );
  }, []);
  const WarehouseList = warehouseList?.results?.map((company) => ({
    value: company.id,
    label: company.location,
  }));
  return (
    <>
      <Modal
        centered
        size="lg"
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Purchase Order Form Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formData ? (
            <>
              {isEditing ? (
                <Form onSubmit={handleSubmit}>
                  <Form.Group controlId="formType">
                    <div className="input-block mb-3 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        State
                      </label>
                      <div className="col-lg-8 position-relative state-search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search state..."
                          value={stateQuery}
                          onChange={(e) => setStateQuery(e.target.value)}
                          onFocus={handleStateFocus}
                        />
                        {stateSuggestions.length > 0 && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{
                              top: "100%",
                              maxHeight: 200,
                              overflowY: "auto",
                              zIndex: 1050,
                            }}
                          >
                            {stateSuggestions.map((state) => (
                              <li
                                key={state.value}
                                className="list-group-item list-group-item-action p-1"
                                onClick={() => {
                                  handleSelectChange(
                                    "state",
                                    state.value,
                                    state.label
                                  );
                                  setStateQuery(state.label);
                                  setStateSuggestions([]);
                                }}
                              >
                                {state.label}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formType">
                    <div className="input-block mb-3 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        Type
                      </label>
                      <div className="col-lg-8">
                        <Select
                          className="form-control"
                          options={typeDropdownData}
                          placeholder="Select Type"
                          onChange={(value, option) =>
                            handleSelectChange("type", value, option.label)
                          }
                          required
                        />
                      </div>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formClient">
                    <div className="input-block mb-3 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        Client
                      </label>
                      <div className="col-lg-8 position-relative client-search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search client..."
                          value={clientQuery}
                          onChange={(e) => setClientQuery(e.target.value)}
                          onFocus={handleClientFocus}
                        />
                        {clientSuggestions.length > 0 && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{
                              top: "100%",
                              maxHeight: 200,
                              overflowY: "auto",
                              zIndex: 1050,
                            }}
                          >
                            {clientSuggestions.map((client) => (
                              <li
                                key={client.id}
                                className="list-group-item list-group-item-action p-1"
                                onClick={() => {
                                  handleSelectChange(
                                    "client",
                                    client.id,
                                    client.name
                                  );
                                  setClientQuery(client.name);
                                  setClientSuggestions([]);
                                }}
                              >
                                {client.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formCompany">
                    <div className="input-block mb-3 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        Company Name
                      </label>
                      <div className="col-lg-8">
                        <Select
                          className="form-control"
                          options={CompanyList}
                          placeholder="Select Company Name"
                          onChange={(value, option) =>
                            handleSelectChange("company", value, option.label)
                          }
                        />
                      </div>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formATMCategory">
                    {formData?.type === "ATM Movement" && (
                      <div className="input-block mb-3 row align-items-center">
                        <label className="col-lg-3 col-form-label mb-0 mt-2">
                          ATM Category
                        </label>
                        <div className="col-lg-8">
                          <Select
                            className="form-control"
                            options={ATMCategory}
                            placeholder="Select ATM Category"
                            onChange={(value, option) =>
                              handleSelectChange(
                                "atm_category",
                                value,
                                option.label
                              )
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group controlId="formATMType">
                    {formData?.type === "ATM Movement" &&
                      formData?.atm_category === "Secondary" && (
                        <div className="input-block mb-3 row align-items-center">
                          <label className="col-lg-3 col-form-label mb-0 mt-2">
                            ATM Type
                          </label>
                          <div className="col-lg-8">
                            <Select
                              className="form-control"
                              options={ATMType}
                              placeholder="Select ATM Type"
                              onChange={(value, option) =>
                                handleSelectChange(
                                  "secondary_type",
                                  value,
                                  option.label
                                )
                              }
                            />
                          </div>
                        </div>
                      )}
                  </Form.Group>
                  <Form.Group controlId="formTransportType">
                    <div className="input-block mb-1 mt-2 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        Transport Type
                      </label>
                      <div className="col-lg-8">
                        <Select
                          className="form-control"
                          options={transportType}
                          placeholder="Select Transport Type"
                          onChange={(value, option) =>
                            handleSelectChange(
                              "transport_type",
                              value,
                              option.label
                            )
                          }
                        />
                      </div>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formTransportType">
                    <div className="input-block mb-1 mt-2 row align-items-center">
                      <label className="col-lg-3 col-form-label mb-0 mt-2">
                        Warehouse Location
                      </label>
                      <div className="col-lg-8 position-relative warehouse-search-container">
                        <Form.Control
                          type="text"
                          placeholder="Search warehouse..."
                          value={warehouseQuery}
                          onChange={(e) => setWarehouseQuery(e.target.value)}
                          onFocus={handleWarehouseFocus}
                        />
                        {warehouseSuggestions.length > 0 && (
                          <ul
                            className="list-group position-absolute w-100"
                            style={{
                              top: "100%",
                              maxHeight: 200,
                              overflowY: "auto",
                              zIndex: 1050,
                            }}
                          >
                            {warehouseSuggestions.map((warehouse) => (
                              <li
                                key={warehouse.id}
                                className="list-group-item list-group-item-action p-1"
                                onClick={() => {
                                  handleSelectChange(
                                    "warehouse",
                                    warehouse.id,
                                    warehouse.location
                                  );
                                  setWarehouseQuery(warehouse.location);
                                  setWarehouseSuggestions([]);
                                }}
                              >
                                {warehouse.location}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </Form.Group>
                  {formData?.type === "ATM Movement" && (
                    <Form.Group controlId="formATMID">
                      <Form.Label className="mb-0 mt-2">ATM ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="atm_id"
                        value={formData.atm_id}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  )}
                  {formData?.type === "ATM Movement" && (
                    <Form.Group controlId="formATMSerial">
                      <Form.Label className="mb-0 mt-2">ATM Serial</Form.Label>
                      <Form.Control
                        type="text"
                        name="atm_sno"
                        value={formData.atm_sno}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  )}
                  {formData?.type === "ATM Movement" && (
                    <Form.Group controlId="formATMSerial">
                      <Form.Label className="mb-0 mt-2">From ATM ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="from_atm"
                        value={formData.from_atm}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  )}
                  {formData?.type === "ATM Movement" && (
                    <Form.Group controlId="formATMSerial">
                      <Form.Label className="mb-0 mt-2">To ATM ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="to_atm"
                        value={formData.to_atm}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  )}
                  <Form.Group controlId="formDocketNumber">
                    <Form.Label className="mb-0 mt-2">Docket Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="client_docket_number"
                      value={formData.client_docket_number}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formInvoiceNumber">
                    <Form.Label className="mb-0 mt-2">
                      Invoice Number
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="client_invoice_number"
                      value={formData.client_invoice_number}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formInvoiceDate">
                    <Form.Label className="mb-0 mt-2">Invoice Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="client_invoice_date"
                      value={formData.client_invoice_date}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formInvoiceValue">
                    <Form.Label className="mb-0 mt-2">Invoice Value</Form.Label>
                    <Form.Control
                      type="text"
                      name="client_invoice_value"
                      value={formData.client_invoice_value}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formEwayBill">
                    <Form.Label className="mb-0 mt-2">E-Way Bill</Form.Label>
                    <Form.Control
                      type="text"
                      name="eway_bill"
                      value={formData.eway_bill}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formFromAddress">
                    <Form.Label className="mb-0 mt-2">From Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="from_name"
                      value={formData.from_name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formFromAddress">
                    <Form.Label className="mb-0 mt-2">From Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="from_address"
                      value={formData.from_address}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formFromPincode">
                    <Form.Label className="mb-0 mt-2">From Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="from_pincode"
                      value={formData.from_pincode}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formFromContact">
                    <Form.Label className="mb-0 mt-2">From Contact</Form.Label>
                    <Form.Control
                      type="text"
                      name="from_contact"
                      value={formData.from_contact}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formFromEmail">
                    <Form.Label className="mb-0 mt-2">From Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="from_email"
                      value={formData.from_email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formToAddress">
                    <Form.Label className="mb-0 mt-2">To Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_name"
                      value={formData.to_name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formToAddress">
                    <Form.Label className="mb-0 mt-2">To Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_address"
                      value={formData.to_address}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formToPincode">
                    <Form.Label className="mb-0 mt-2">To Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_pincode"
                      value={formData.to_pincode}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formToContact">
                    <Form.Label className="mb-0 mt-2">To Contact</Form.Label>
                    <Form.Control
                      type="text"
                      name="to_contact"
                      value={formData.to_contact}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formToEmail">
                    <Form.Label className="mb-0 mt-2">To Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="to_email"
                      value={formData.to_email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formReceiver">
                    <Form.Label className="mb-0 mt-2">Receiver</Form.Label>
                    <Form.Control
                      type="text"
                      name="receiver"
                      value={formData.receiver}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formSecondaryType">
                    <Form.Label className="mb-0 mt-2">
                      Secondary Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="secondary_type"
                      value={formData.secondary_type}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formSubclientName">
                    <Form.Label className="mb-0 mt-2">
                      Subclient Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="subclient_name"
                      value={formData.subclient_name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group controlId="formRemarks">
                    <Form.Label className="mb-0 mt-2">Remarks</Form.Label>
                    <Form.Control
                      type="text"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  {/* {formData.items && formData.items.length > 0 && (
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Weight</th>
                          <th>Pieces</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((product) => (
                          <tr key={product.id}>
                            <td>{product?.name?.name || "N/A"}</td>
                            <td>{product.weight || "N/A"}</td>
                            <td>{product.pieces || "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )} */}

                  {/* <Button className="mt-3" variant="primary" type="submit">
                    Save Changes
                  </Button> */}
                </Form>
              ) : (
                <Table striped bordered hover>
                  <tbody>
                    <tr>
                      <th>Created Date</th>
                      <td>
                        {formData.created_at
                          ? new Date(formData.created_at).toLocaleString()
                          : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>State</th>
                      <td>{formData.state ? formData.state : "NA"}</td>
                    </tr>
                    <tr>
                      <th>Type</th>
                      <td>{formData.type ? formData.type : "NA"}</td>
                    </tr>
                    <tr>
                      <th>Client</th>
                      <td>
                        {formData.client_details?.name
                          ? formData.client_details?.name
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Company</th>
                      <td>
                        {formData.company_details?.name
                          ? formData.company_details?.name
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Warehouse Location</th>
                      <td>
                        {formData.warehouse_details?.location
                          ? formData.warehouse_details?.location
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Docket Number</th>
                      <td>
                        {formData.client_docket_number
                          ? formData.client_docket_number
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Invoice Number</th>
                      <td>
                        {formData.client_invoice_number
                          ? formData.client_invoice_number
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Invoice Date</th>
                      <td>
                        {formData.client_invoice_date
                          ? formData.client_invoice_date
                          : "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>Invoice Value</th>
                      <td>
                        {formData.client_invoice_value
                          ? formData.client_invoice_value
                          : "NA"}
                      </td>
                    </tr>
                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>ATM Category</th>
                        <td>
                          {formData.atm_category ? formData.atm_category : "NA"}
                        </td>
                      </tr>
                    )}

                    {formData?.type === "ATM Movement" &&
                      formData?.atm_category === "Secondary" && (
                        <tr>
                          <th>Secondary Type</th>
                          <td>
                            {formData.secondary_type
                              ? formData.secondary_type
                              : "NA"}
                          </td>
                        </tr>
                      )}

                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>ATM ID</th>
                        <td>{formData.atm_id ? formData.atm_id : "NA"}</td>
                      </tr>
                    )}
                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>ATM S.No</th>
                        <td>{formData.atm_sno ? formData.atm_sno : "NA"}</td>
                      </tr>
                    )}
                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>To ATM ID</th>
                        <td>{formData.to_atm ? formData.to_atm : "NA"}</td>
                      </tr>
                    )}
                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>From ATM ID</th>
                        <td>{formData.from_atm ? formData.from_atm : "NA"}</td>
                      </tr>
                    )}
                    {formData?.type === "ATM Movement" && (
                      <tr>
                        <th>E-Way Bill</th>
                        <td>
                          {formData.eway_bill ? formData.eway_bill : "NA"}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <th>From Name</th>
                      <td>{formData.from_name ? formData.from_name : "NA"}</td>
                    </tr>
                    <tr>
                      <th>From Address</th>
                      <td>
                        {formData.from_address ? formData.from_address : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>From Pincode</th>
                      <td>
                        {formData.from_pincode ? formData.from_pincode : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>From Contact</th>
                      <td>
                        {formData.from_contact ? formData.from_contact : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>From Email</th>
                      <td>
                        {formData.from_email ? formData.from_email : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>To Name</th>
                      <td>{formData.to_name ? formData.to_name : "NA"}</td>
                    </tr>
                    <tr>
                      <th>To Address</th>
                      <td>
                        {formData.to_address ? formData.to_address : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>To Pincode</th>
                      <td>
                        {formData.to_pincode ? formData.to_pincode : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>To Contact</th>
                      <td>
                        {formData.to_contact ? formData.to_contact : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>To Email</th>
                      <td>{formData.to_email ? formData.to_email : "NA"}</td>
                    </tr>
                    <tr>
                      <th>Receiver</th>
                      <td>{formData.receiver ? formData.receiver : "NA"}</td>
                    </tr>

                    <tr>
                      <th>Subclient Name</th>
                      <td>
                        {formData.subclient_name
                          ? formData.subclient_name
                          : "NA"}
                      </td>
                    </tr>
                    <tr>
                      <th>Transport Type</th>
                      <td>
                        {formData.transport_type
                          ? formData.transport_type
                          : "NA"}
                      </td>
                    </tr>

                    <tr>
                      <th>Remarks</th>
                      <td>{formData.remarks ? formData.remarks : "NA"}</td>
                    </tr>
                    <tr>
                      <th>Products</th>
                      <td>
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Pieces</th>
                              <th>Weight</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData?.items_po?.map((product, index) => (
                              <tr key={index}>
                                <td>{product?.name?.name}</td>
                                <td>{product.number}</td>
                                <td>{product.weight}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
              <div className="d-flex justify-content-end mt-2">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  className="me-2"
                >
                  Close
                </Button>
                {formData.is_completed === false && isEditing ? (
                  <Button variant="success" onClick={handleSubmit}>
                    Save
                  </Button>
                ) : (
                  formData.is_completed === false && (
                    <Button variant="primary" onClick={handleEditToggle}>
                      Edit
                    </Button>
                  )
                )}
                {/* {formData.is_completed === false && (
                  <Button
                    variant="danger"
                    onClick={openDeleteConfirmation}
                    className="ms-2"
                  >
                    Delete
                  </Button>
                )} */}
              </div>
            </>
          ) : (
            <p>
              {" "}
              loading...{" "}
              <Loader
                className="feather-loader"
                data-bs-toggle="tooltip"
                title="feather-loader"
              />
            </p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteConfirmation}
        onHide={closeDeleteConfirmation}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this outward form?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteConfirmation}>
            Cancel
          </Button>
          {/* <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button> */}
        </Modal.Footer>
      </Modal>
    </>
  );
}

const PurchaseOrderList = () => {
  const [clientDocket, setClientDocket] = useState();
  const [VTDocket, setVTDocket] = useState();
  const [inwardList, setInwardList] = useState([]);
  const [show, setShow] = useState(false);
  const [showAddInward, setShowAddInward] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [warehouse, setWarehouse] = useState([]);
  const [selectedClient, setSelectedClient] = useState({
    id: "",
    name: "Select Client",
  });
  const [selectedCompany, setSelectedCompany] = useState({
    id: "",
    name: "Select Company",
  });
  const [selectedWarehouse, setSelectedWarehouse] = useState({
    id: "",
    name: "Select warehouse",
  });
  console.log(selectedWarehouse);
  const [state, setState] = useState(""); // State for type filter
  const [type, setType] = useState(""); // State for type filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [approvals, setApprovals] = useState("");
  const [atmId, setAtmId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  //  state for checkboxes
  const [selectedPOs, setSelectedPOs] = useState([]); // Stores selected PO IDs
  const [selectAll, setSelectAll] = useState(false); // State for select all checkbox

  //  loading state for downloads
  const [isDownloading, setIsDownloading] = useState(false); // State for download process
  const [fromVTDocket, setFromVTDocket] = useState();
  const [toVTDocket, setToVTDocket] = useState();
  console.log(selectedPOs);

  // Search states for main filter section
  const [filterClientQuery, setFilterClientQuery] = useState("");
  const [filterClientSuggestions, setFilterClientSuggestions] = useState([]);
  const [filterStateQuery, setFilterStateQuery] = useState("");
  const [filterStateSuggestions, setFilterStateSuggestions] = useState([]);

  const handleClose = () => setShow(false);
  const handleShow = (id) => setShow(id);
  const handleShowAddInward = (id) => setShowAddInward(id);
  const handleCloseAddInward = () => setShowAddInward(false);

  const [warehouseQuery, setWarehouseQuery] = useState("");
  const [warehouseSuggestions, setWarehouseSuggestions] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchClients();
    fetchCompanies();
    fetchInwardList();
  }, []);

  // Fetch filter client suggestions
  const fetchFilterClientSuggestions = async (query) => {
    try {
      const { data } = await axiosInstance.get(LogisticsClientsURL, {
        params: { name: query },
      });
      setFilterClientSuggestions(data.results || []);
    } catch (err) {
      console.error("Filter client search error:", err);
      setFilterClientSuggestions([]);
    }
  };

  // Filter state suggestions for main filter
  const filterStateSuggestionsForFilter = (query) => {
    if (!query) {
      return stateNames;
    }
    return stateNames.filter((state) =>
      state.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Debounce filter client search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (filterClientQuery.length) {
        fetchFilterClientSuggestions(filterClientQuery);
      } else {
        // Don't show suggestions on empty query unless focused
        setFilterClientSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [filterClientQuery]);

  // Debounce filter state search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (filterStateQuery.length) {
        const filteredStates =
          filterStateSuggestionsForFilter(filterStateQuery);
        setFilterStateSuggestions(filteredStates);
      } else {
        // Don't show suggestions on empty query unless focused
        setFilterStateSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [filterStateQuery]);

  // Show initial suggestions when input is focused
  const handleFilterClientFocus = () => {
    fetchFilterClientSuggestions("");
  };

  const handleFilterStateFocus = () => {
    setFilterStateSuggestions(stateNames);
  };

  // Clear suggestions when clicking outside
  const handleFilterClickOutside = (e) => {
    if (
      !e.target.closest(".filter-client-search-container") &&
      !e.target.closest(".filter-state-search-container") &&
      !e.target.closest(".warehouse-search-container")
    ) {
      setFilterClientSuggestions([]);
      setFilterStateSuggestions([]);
      setWarehouseSuggestions([]);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleFilterClickOutside);
    return () => {
      document.removeEventListener("click", handleFilterClickOutside);
    };
  }, []);

  // fetch warehouse data
  useEffect(() => {
    axiosInstance.get(`${LogisticsWarehouse}`).then(
      (response) => {
        console.log(response.data);
        setWarehouse(response.data);
      },
      (error) => {
        console.log(error);
      }
    );
  }, []);
  // const WarehouseList = warehouse?.results?.map((company) => ({
  //   value: company.id,
  //   label: company.location,
  // }));
  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get(LogisticsClientsURL);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients: ", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get(LogisticsCompaniesURL);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies: ", error);
    }
  };

  const fetchInwardList = async (filters = {}, page = 1) => {
    setIsLoading(true);
    console.log(filters);
    try {
      const response = await axiosInstance.get(LogisticsPOInward, {
        params: { ...filters, page },
      });

      console.log(response.data);
      setInwardList(response.data);
      setTotalPages(Math.ceil(response.data.count / 20)); // Assuming 20 items per page
    } catch (error) {
      console.error("Error fetching inward list: ", error);
    }
    setIsLoading(false);
  };
  console.log(type);

// Corrected useEffect Hook
useEffect(() => {
  // Pass the current page to the filter handler
  handleFilterChange(currentPage);
}, [
  selectedClient.id,
  selectedCompany.id,
  selectedWarehouse.id,
  startDate,
  endDate,
  atmId,
  approvals,
  clientDocket,
  type,
  state,
  VTDocket,
  toVTDocket,
  fromVTDocket,
  currentPage, // Add currentPage to the dependency array
]);

// Corrected handleFilterChange function
const handleFilterChange = (page = 1) => {
  const filters = {
    client: selectedClient.id,
    company: selectedCompany.id,
    warehouse: selectedWarehouse.id,
    start_date: startDate,
    end_date: endDate,
    is_completed: approvals,
    atm_id: atmId,
    type: type,
    client_docket_number: clientDocket,
    vt_docket_number: VTDocket,
    state: state,
    from_vt: fromVTDocket,
    to_vt: toVTDocket,
  };
  // Pass the filters and the current page to fetchInwardList
  fetchInwardList(filters, page);
};

// Corrected handlePageChange function
const handlePageChange = (newPage) => {
  setCurrentPage(newPage); // Set the new page
  // The useEffect hook will now handle the data fetch
};
  const handleResetFilters = () => {
    setSelectedClient({ id: "", name: "Select Client" });
    setSelectedCompany({ id: "", name: "Select Company" });
    setSelectedWarehouse({ id: "", name: "Select Warehouse" });
    setStartDate("");
    setEndDate("");
    setApprovals("");
    setAtmId("");
    fetchInwardList({}, 1);
    setType(""); // Reset type filter
    setClientDocket("");
    setVTDocket("");
    setState("");
    setFromVTDocket("");
    setToVTDocket("");

    // Clear search bar data
    setFilterClientQuery("");
    setFilterStateQuery("");
    setWarehouseQuery("");
    setFilterClientSuggestions([]);
    setFilterStateSuggestions([]);
    setWarehouseSuggestions([]);
  };


  const handleExport = async (filters = {}) => {
    try {
      console.log("Raw filters received:", filters); // Debug: See exactly what we receive

      // Determine which endpoint to use based on showInOutData checkbox
      const endpoint = filters.showInOutData ? exportPOInward : exportPO;

      // Prepare parameters based on the endpoint
      let params = {};

      if (filters.showInOutData) {
        // For exportPOInward, use filters passed from modal
        params = {
          company_name: filters.selectedCompany || "",
          client: filters.selectedClient || "",
          start_date: filters.startDate || "",
          end_date: filters.endDate || "",
        };
      } else {
        // For exportPO, use basic filters
        params = {
          type: filters.type,
          company_name: filters.selectedCompany,
          client: filters.selectedClient,
          start_date: filters.startDate,
          end_date: filters.endDate,
        };
      }

      console.log("Export params:", params); // Debug log
      console.log("Filters received:", filters); // Debug log
      console.log("Modal startDate:", filters.startDate); // Debug log
      console.log("Modal endDate:", filters.endDate); // Debug log
      console.log("Modal selectedCompany:", filters.selectedCompany); // Debug log
      console.log("Modal selectedClient:", filters.selectedClient); // Debug log

      const response = await axiosInstance.get(endpoint, {
        params,
        responseType: "blob",
        timeout: 0,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Set filename based on export type
      const filename = filters.showInOutData
        ? "PO_Inward_Outward_Data.xlsx"
        : "PO_data.xlsx";
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting data: ", error);
    }
  };

  const updateOutwardInList = (updatedOutward) => {
    setInwardList((prevList) => {
      return {
        ...prevList,
        results: prevList?.results?.map((outward) =>
          outward.id === updatedOutward.id ? updatedOutward : outward
        ),
      };
    });
  };
  const stateNames = [
    // States
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Arunachal Pradesh", label: "Arunachal Pradesh" },
    { value: "Assam", label: "Assam" },
    { value: "Bihar", label: "Bihar" },
    { value: "Chhattisgarh", label: "Chhattisgarh" },
    { value: "Goa", label: "Goa" },
    { value: "Gujarat", label: "Gujarat" },
    { value: "Haryana", label: "Haryana" },
    { value: "Himachal Pradesh", label: "Himachal Pradesh" },
    { value: "Jharkhand", label: "Jharkhand" },
    { value: "Karnataka", label: "Karnataka" },
    { value: "Kerala", label: "Kerala" },
    { value: "Madhya Pradesh", label: "Madhya Pradesh" },
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Manipur", label: "Manipur" },
    { value: "Meghalaya", label: "Meghalaya" },
    { value: "Mizoram", label: "Mizoram" },
    { value: "Nagaland", label: "Nagaland" },
    { value: "Odisha", label: "Odisha" },
    { value: "Punjab", label: "Punjab" },
    { value: "Rajasthan", label: "Rajasthan" },
    { value: "Sikkim", label: "Sikkim" },
    { value: "Tamil Nadu", label: "Tamil Nadu" },
    { value: "Telangana", label: "Telangana" },
    { value: "Tripura", label: "Tripura" },
    { value: "Uttar Pradesh", label: "Uttar Pradesh" },
    { value: "Uttarakhand", label: "Uttarakhand" },
    { value: "West Bengal", label: "West Bengal" },
    // Union Territories
    {
      value: "Andaman and Nicobar Islands",
      label: "Andaman and Nicobar Islands",
    },
    { value: "Chandigarh", label: "Chandigarh" },
    { value: "Delhi", label: "Delhi" },
    {
      value: "Dadra and Nagar Haveli and Daman & Diu",
      label: "Dadra and Nagar Haveli and Daman & Diu",
    },
    { value: "Jammu & Kashmir", label: "Jammu & Kashmir" },
    { value: "Ladakh", label: "Ladakh" },
    { value: "Lakshadweep", label: "Lakshadweep" },
    { value: "Puducherry", label: "Puducherry" },
  ];

  const handleDocketDownload = async (item) => {
    console.log(item);
    try {
      const response = await axiosInstance.get(
        `${LogisticsPOPdf}${item?.id}/`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${item?.vt_docket_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting data: ", error);
    }
  };
  //  function to handle multiple docket download
  const handleMultipleDocketDownload = async () => {
    if (selectedPOs.length > 0) {
      setIsDownloading(true); // Start loader
      const ids = selectedPOs.join(",");
      try {
        const response = await axiosInstance.get(
          `/logistics/download-multiple-pdfs/${ids}/`,
          {
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `dockets_${ids}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error("Error downloading multiple dockets: ", error);
      } finally {
        setIsDownloading(false); // Stop loader after the download completes
      }
    }
  };
  //  function to handle select all checkboxes
  const handleSelectAll = () => {
    if (!selectAll) {
      const allPOs = inwardList?.results?.map((item) => item.id);
      setSelectedPOs(allPOs);
    } else {
      setSelectedPOs([]);
    }
    setSelectAll(!selectAll);
  };

  //  function to handle individual PO selection
  const handleSelectPO = (id) => {
    setSelectedPOs((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((poId) => poId !== id)
        : [...prevSelected, id]
    );
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      try {
        await axiosInstance.delete(`${LogisticsPOInward}${itemToDelete.id}/`);
        toast.success("Purchase order deleted successfully");
        // Remove the item from the list
        setInwardList((prevList) => ({
          ...prevList,
          results: prevList.results.filter(
            (item) => item.id !== itemToDelete.id
          ),
          count: prevList.count - 1,
        }));
        setShowDeleteModal(false);
        setItemToDelete(null);
      } catch (error) {
        console.error("Error deleting purchase order: ", error);
        toast.error("Failed to delete purchase order");
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Fetch warehouse matches by location
  const fetchWarehouseSuggestions = async (query) => {
    try {
      const { data } = await axiosInstance.get(LogisticsWarehouse, {
        params: { location: query },
      });
      setWarehouseSuggestions(data.results || []);
    } catch (err) {
      console.error("Warehouse search error:", err);
      setWarehouseSuggestions([]);
    }
  };

  // Debounce user input
  useEffect(() => {
    const handler = setTimeout(() => {
      if (warehouseQuery.length) {
        fetchWarehouseSuggestions(warehouseQuery);
      } else {
        setWarehouseSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [warehouseQuery]);

  return (
    <>
      <Header />
      <AdminSideBar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-start mb-0">Purchase Order List</h2>
            <div>
              <Button
                onClick={() => setShowExportModal(true)}
                className="btn-sm"
                style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
              >
                <i className="fas fa-file-excel me-1"></i>
                Export to Excel
              </Button>
              <Button
                className="ms-2 btn-sm"
                onClick={handleMultipleDocketDownload}
                disabled={isDownloading || selectedPOs.length === 0}
                style={{
                  backgroundColor: "#ffc107",
                  borderColor: "#ffc107",
                  color: "#212529",
                }}
              >
                {isDownloading ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : selectedPOs.length === 0 ? (
                  <>
                    <i className="fas fa-download me-1"></i>
                    Docket Not Selected
                  </>
                ) : (
                  <>
                    <i className="fas fa-download me-1"></i>
                    Download Selected Dockets ({selectedPOs?.length})
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Filter Section */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              {/* Primary Filters Row */}
              <div className="row mb-0">
                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Client
                  </label>
                  <div className="position-relative filter-client-search-container">
                    <Form.Control
                      type="text"
                      placeholder="Search client..."
                      value={filterClientQuery}
                      onChange={(e) => setFilterClientQuery(e.target.value)}
                      onFocus={handleFilterClientFocus}
                      className="form-control-sm"
                    />
                    {filterClientSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100"
                        style={{
                          top: "100%",
                          maxHeight: 200,
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        {filterClientSuggestions.map((client) => (
                          <li
                            key={client.id}
                            className="list-group-item list-group-item-action p-2"
                            onClick={() => {
                              setSelectedClient({
                                id: client.id,
                                name: client.name,
                              });
                              setFilterClientQuery(client.name);
                              setFilterClientSuggestions([]);
                            }}
                          >
                            {client.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Company
                  </label>
                  <Dropdown
                    onSelect={(e) =>
                      setSelectedCompany({
                        id: e,
                        name:
                          companies?.results?.find(
                            (company) => company.id === parseInt(e)
                          )?.name || "Select Company",
                      })
                    }
                  >
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="dropdown-basic"
                      className="w-100 text-start form-control-sm d-flex justify-content-between align-items-center"
                    >
                      {selectedCompany.name}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {companies?.results?.map((company) => (
                        <Dropdown.Item key={company.id} eventKey={company.id}>
                          {company.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    State
                  </label>
                  <div className="position-relative filter-state-search-container">
                    <Form.Control
                      type="text"
                      placeholder="Search state..."
                      value={filterStateQuery}
                      onChange={(e) => setFilterStateQuery(e.target.value)}
                      onFocus={handleFilterStateFocus}
                      className="form-control-sm"
                    />
                    {filterStateSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100"
                        style={{
                          top: "100%",
                          maxHeight: 200,
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        {filterStateSuggestions.map((state) => (
                          <li
                            key={state.value}
                            className="list-group-item list-group-item-action p-2"
                            onClick={() => {
                              setState(state.value);
                              setFilterStateQuery(state.label);
                              setFilterStateSuggestions([]);
                            }}
                          >
                            {state.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Type
                  </label>
                  <Dropdown onSelect={(e) => setType(e)}>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="dropdown-basic"
                      className="w-100 text-start form-control-sm d-flex justify-content-between align-items-center"
                    >
                      {type ? type : "Select Type"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <Dropdown.Item eventKey="ATM Movement">
                        ATM Movement
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="Household">
                        Household
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="3PL">3PL</Dropdown.Item>
                      <Dropdown.Item eventKey="Other">Other</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              {/* Secondary Filters Row */}
              <div className="row mb-0">
                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Status
                  </label>
                  <Dropdown onSelect={(e) => setApprovals(e)}>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      id="dropdown-basic"
                      className="w-100 text-start form-control-sm d-flex justify-content-between align-items-center"
                    >
                      <span>
                        {approvals
                          ? approvals === "true"
                            ? "Completed Inward"
                            : "Pending Inward"
                          : "Select Status"}
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      <Dropdown.Item eventKey="true">
                        Completed Inward
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="false">
                        Pending Inward
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Warehouse
                  </label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search warehouse…"
                      value={warehouseQuery}
                      onChange={(e) => {
                        setWarehouseQuery(e.target.value);
                        setSelectedWarehouse({ id: "", name: e.target.value });
                      }}
                      className="form-control-sm"
                    />
                    {warehouseSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100"
                        style={{
                          top: "100%",
                          maxHeight: 200,
                          overflowY: "auto",
                          zIndex: 1050,
                        }}
                      >
                        {warehouseSuggestions.map((w) => (
                          <li
                            key={w.id}
                            className="list-group-item list-group-item-action p-2"
                            onClick={() => {
                              setSelectedWarehouse({
                                id: w.id,
                                name: w.location,
                              });
                              setWarehouseQuery(w.location);
                              setWarehouseSuggestions([]);
                            }}
                          >
                            {w.location}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    VT Docket Number
                  </label>
                  <Form.Control
                    type="text"
                    value={VTDocket}
                    onChange={(e) => setVTDocket(e.target.value)}
                    placeholder="Enter VT Docket Number"
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-3 mb-1">
                  <label className="form-label text-muted small mb-1">
                    ATM ID
                  </label>
                  <Form.Control
                    type="text"
                    value={atmId}
                    onChange={(e) => setAtmId(e.target.value)}
                    placeholder="Enter ATM ID"
                    className="form-control-sm"
                  />
                </div>
              </div>

              {/* Date Range and Additional Filters */}
              <div className="row mb-0">
                <div className="col-md-2 mb-1">
                  <label className="form-label text-muted small mb-1">
                    From VT Docket
                  </label>
                  <Form.Control
                    type="text"
                    value={fromVTDocket}
                    onChange={(e) => setFromVTDocket(e.target.value)}
                    placeholder="From VT Docket"
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-2 mb-1">
                  <label className="form-label text-muted small mb-1">
                    To VT Docket
                  </label>
                  <Form.Control
                    type="text"
                    value={toVTDocket}
                    onChange={(e) => setToVTDocket(e.target.value)}
                    placeholder="To VT Docket"
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-2 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Client Docket
                  </label>
                  <Form.Control
                    type="text"
                    value={clientDocket}
                    onChange={(e) => setClientDocket(e.target.value)}
                    placeholder="Client Docket Number"
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-2 mb-1">
                  <label className="form-label text-muted small mb-1">
                    Start Date
                  </label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-2 mb-1">
                  <label className="form-label text-muted small mb-1">
                    End Date
                  </label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control-sm"
                  />
                </div>

                <div className="col-md-2 mb-1 d-flex align-items-end">
                  <div className="d-flex gap-2 w-100">
                    <Button
                      variant="primary"
                      onClick={handleFilterChange}
                      className="btn-sm flex-fill"
                    >
                      <i className="fas fa-search me-1"></i>
                      Apply
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handleResetFilters}
                      className="btn-sm flex-fill"
                    >
                      <i className="fas fa-undo me-1"></i>
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <table className="table table-striped text-center">
            <thead className="table-secondary">
              <tr>
                <th>
                  <input
                    className="me-1"
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                  Select All
                </th>
                <th>No.</th>
                <th>Client Name</th>
                <th>Type</th>
                <th>Client Docket Number</th>
                <th>VT Docket Number</th>
                <th>Warehouse Location</th>
                <th>ATM ID</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading ? (
                inwardList?.count > 0 ? (
                  inwardList?.results?.map((item, index) => (
                    <tr key={item.id}>
                      <td className="">
                        <input
                          type="checkbox"
                          checked={selectedPOs.includes(item.id)}
                          onChange={() => handleSelectPO(item.id)}
                        />
                      </td>
                      <td>{(currentPage - 1) * 20 + index + 1}</td>
                      <td>
                        {item?.client_details?.name
                          ? item?.client_details?.name
                          : "Not available"}
                      </td>
                      <td>{item?.type ? item.type : "Not available"}</td>
                      <td>
                        {item?.client_docket_number
                          ? item.client_docket_number
                          : "Not Available"}
                      </td>
                      <td>
                        {item?.vt_docket_number
                          ? item.vt_docket_number
                          : "Not Available"}
                      </td>
                      <td>
                        {item?.warehouse_details
                          ? item.warehouse_details?.location
                          : "Not Available"}
                      </td>
                      <td>{item?.atm_id ? item?.atm_id : "Not Available"}</td>
                      <td>
                        {item?.created_at
                          ? item?.created_at?.slice(0, 10)
                          : "Not Available"}
                      </td>
                      <td>
                        <Button
                          className="btn btn-primary btn-sm ms-1 mb-1"
                          variant="primary"
                          onClick={() => handleShow(item.id)}
                        >
                          View Details
                        </Button>
                        {item.is_completed === true ? (
                          <div
                            variant="success"
                            className="btn btn-success btn-sm ms-1"
                          >
                            Inward done
                          </div>
                        ) : (
                          <Button
                            className="btn btn-primary btn-sm ms-1"
                            variant="primary"
                            onClick={() => handleShowAddInward(item.id)}
                          >
                            Add Inward
                          </Button>
                        )}
                        <Button
                          className="btn btn-primary btn-sm ms-1 mt-1"
                          onClick={() => handleDocketDownload(item)}
                        >
                          <Download
                            height={15}
                            width={15}
                            className="feather-download  mb-1 my-0 "
                            data-bs-toggle="tooltip"
                            title="feather-download"
                          />{" "}
                          Docket
                        </Button>
                        {item.is_completed === false && (
                          <Button
                            className="btn btn-danger btn-sm ms-1 mt-1"
                            variant="danger"
                            onClick={() => handleDeleteClick(item)}
                          >
                            <Trash2
                              height={15}
                              width={15}
                              className="feather-trash-2 mb-1 my-0"
                              data-bs-toggle="tooltip"
                              title="feather-trash-2"
                            />{" "}
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">No data</td>
                  </tr>
                )
              ) : (
                <tr>
                  <td colSpan="10">
                    {" "}
                    loading...{" "}
                    <Loader
                      className="feather-loader"
                      data-bs-toggle="tooltip"
                      title="feather-loader"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="d-flex justify-content-between">
            <Button
              variant="secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
          <ViewDetailsModal
            updateOutwardInList={updateOutwardInList}
            show={show}
            handleClose={handleClose}
          />
          <POExportModal
            show={showExportModal}
            handleClose={() => setShowExportModal(false)}
            handleExport={handleExport}
            currentFilters={{
              client: selectedClient.id,
              company: selectedCompany.id,
              warehouse: selectedWarehouse.id,
              startDate,
              endDate,
              approvals,
              atmId,
              type,
              clientDocket,
              VTDocket,
              state,
              fromVTDocket,
              toVTDocket,
              selectedWarehouse,
            }}
          />
          <AddInwardModal
            show={showAddInward}
            handleClose={handleCloseAddInward}
            updateOutwardInList={updateOutwardInList}
          />

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this purchase order?</p>
              <p className="text-muted">
                <strong>Client:</strong>{" "}
                {itemToDelete?.client_details?.name || "N/A"}
                <br />
                <strong>Docket Number:</strong>{" "}
                {itemToDelete?.client_docket_number || "N/A"}
                <br />
                <strong>VT Docket:</strong>{" "}
                {itemToDelete?.vt_docket_number || "N/A"}
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleDeleteCancel}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderList;
